import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { HerracContext } from '../../App';
import ProfilePictureUpload from './ProfilePictureUpload';
import { 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaLock, 
  FaEye, 
  FaEyeSlash, 
  FaSave,
  FaCheckCircle,
  FaExclamationCircle,
  FaWallet
} from 'react-icons/fa';

function UserSettings() {
  const { user, setUser, balance, setBalance } = useContext(HerracContext);

  // Form States
  const [formData, setFormData] = useState({
    userId: '',
    fullName: '',
    email: '',
    phoneNumber: '',
    newPassword: '',
    confirmPassword: '',
  });

  // UI States
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' | 'security'

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFormData({
        userId: user.userId || user.id || '',
        fullName: user.fullName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        newPassword: '',
        confirmPassword: '',
      });
      setBalance(user.balance || 0);
    }
  }, [user, setBalance]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setMessage({ type: '', text: '' });
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(prev => !prev);
    } else {
      setShowConfirmPassword(prev => !prev);
    }
  };

  // Validate form
  const validateForm = () => {
    if (!formData.fullName.trim()) {
      setMessage({ type: 'error', text: 'Ad Soyad daxil edilməlidir' });
      return false;
    }

    if (!formData.email.trim()) {
      setMessage({ type: 'error', text: 'E-poçt daxil edilməlidir' });
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage({ type: 'error', text: 'Düzgün e-poçt ünvanı daxil edin' });
      return false;
    }

    // Password validation (if changing)
    if (formData.newPassword) {
      if (formData.newPassword.length < 6) {
        setMessage({ type: 'error', text: 'Şifrə ən azı 6 simvoldan ibarət olmalıdır' });
        return false;
      }

      if (formData.newPassword !== formData.confirmPassword) {
        setMessage({ type: 'error', text: 'Şifrələr uyğun gəlmir' });
        return false;
      }
    }

    return true;
  };

  // Handle form submission
  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const updateData = {
        userId: formData.userId,
        fullName: formData.fullName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
      };

      // Only include password if it's being changed
      if (formData.newPassword) {
        updateData.newPassword = formData.newPassword;
      }

      const response = await axios.put(
        'https://api.herracim.az/api/user/update',
        updateData,
        { withCredentials: true }
      );

      const updatedUser = response.data;

      // Update context
      setUser(updatedUser);
      setBalance(updatedUser.balance || balance);

      // Clear password fields
      setFormData(prev => ({
        ...prev,
        newPassword: '',
        confirmPassword: '',
      }));

      setMessage({ 
        type: 'success', 
        text: 'Məlumatlar uğurla yeniləndi!' 
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Profili yeniləmək alınmadı:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Xəta baş verdi. Yenidən cəhd edin.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-settings">
      {/* Header */}
      <div className="user-settings__header">
        <h2 className="user-settings__title">İstifadəçi Tənzimləmələri</h2>
        <p className="user-settings__subtitle">Profil məlumatlarınızı idarə edin</p>
      </div>

      {/* Tabs */}
      <div className="user-settings__tabs">
        <button
          className={`user-settings__tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <FaUser />
          <span>Profil Məlumatları</span>
        </button>
        <button
          className={`user-settings__tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          <FaLock />
          <span>Təhlükəsizlik</span>
        </button>
      </div>

      {/* Message Alert */}
      {message.text && (
        <div className={`user-settings__message user-settings__message--${message.type}`}>
          {message.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
          <span>{message.text}</span>
        </div>
      )}

      <form onSubmit={handleUpdateProfile} className="user-settings__form">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="user-settings__section">
            {/* Profile Picture */}
            <ProfilePictureUpload />

            {/* User ID (Read-only) */}
            <div className="user-settings__field">
              <label className="user-settings__label">
                <FaUser />
                İstifadəçi ID
              </label>
              <input
                type="text"
                className="user-settings__input user-settings__input--readonly"
                value={formData.userId}
                readOnly
              />
            </div>

            {/* Balance (Read-only) */}
            <div className="user-settings__field">
              <label className="user-settings__label">
                <FaWallet />
                Balans
              </label>
              <input
                type="text"
                className="user-settings__input user-settings__input--readonly user-settings__input--balance"
                value={`${balance.toFixed(2)} AZN`}
                readOnly
              />
            </div>

            {/* Full Name */}
            <div className="user-settings__field">
              <label className="user-settings__label">
                <FaUser />
                Ad Soyad <span className="user-settings__required">*</span>
              </label>
              <input
                type="text"
                name="fullName"
                className="user-settings__input"
                placeholder="Adınız və Soyadınız"
                value={formData.fullName}
                onChange={handleChange}
                required
              />
            </div>

            {/* Email */}
            <div className="user-settings__field">
              <label className="user-settings__label">
                <FaEnvelope />
                E-poçt <span className="user-settings__required">*</span>
              </label>
              <input
                type="email"
                name="email"
                className="user-settings__input"
                placeholder="email@example.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            {/* Phone */}
            <div className="user-settings__field">
              <label className="user-settings__label">
                <FaPhone />
                Telefon nömrəsi
              </label>
              <input
                type="tel"
                name="phoneNumber"
                className="user-settings__input"
                placeholder="+994 XX XXX XX XX"
                value={formData.phoneNumber}
                onChange={handleChange}
              />
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="user-settings__section">
            <div className="user-settings__security-notice">
              <FaLock />
              <div>
                <h4>Şifrə Dəyişikliyi</h4>
                <p>Yeni şifrə ən azı 6 simvoldan ibarət olmalıdır</p>
              </div>
            </div>

            {/* New Password */}
            <div className="user-settings__field">
              <label className="user-settings__label">
                <FaLock />
                Yeni Şifrə
              </label>
              <div className="user-settings__password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="newPassword"
                  className="user-settings__input"
                  placeholder="Yeni şifrənizi daxil edin"
                  value={formData.newPassword}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="user-settings__password-toggle"
                  onClick={() => togglePasswordVisibility('password')}
                  aria-label="Şifrəni göstər/gizlət"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="user-settings__field">
              <label className="user-settings__label">
                <FaLock />
                Şifrəni Təsdiq Et
              </label>
              <div className="user-settings__password-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  className="user-settings__input"
                  placeholder="Şifrəni təkrar daxil edin"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="user-settings__password-toggle"
                  onClick={() => togglePasswordVisibility('confirm')}
                  aria-label="Şifrəni göstər/gizlət"
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {formData.newPassword && (
              <div className="user-settings__password-strength">
                <div className="user-settings__password-strength-bar">
                  <div 
                    className={`user-settings__password-strength-fill ${
                      formData.newPassword.length >= 8 ? 'strong' : 
                      formData.newPassword.length >= 6 ? 'medium' : 'weak'
                    }`}
                  ></div>
                </div>
                <span className="user-settings__password-strength-text">
                  {formData.newPassword.length >= 8 ? 'Güclü şifrə' : 
                   formData.newPassword.length >= 6 ? 'Orta səviyyə' : 'Zəif şifrə'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div className="user-settings__actions">
          <button
            type="submit"
            className="user-settings__submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="user-settings__spinner"></span>
                Yenilənir...
              </>
            ) : (
              <>
                <FaSave />
                Dəyişiklikləri Yadda Saxla
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default UserSettings;